import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {PurchaseOrderItemCollection} from './purchase-order-item-collection';
import {map} from 'rxjs/operators';
import {PurchaseOrderItem} from './purchase-order-item';
import * as classTransformer from 'class-transformer';

export interface DateRangeFilterValue {
    from?: string;
    to?: string;
}

type FilterValue = string | number | Date | DateRangeFilterValue | string[];

@Injectable({
    providedIn: 'root',
})
export class PurchaseOrderItemService {
    constructor(
        protected httpClient: HttpClient,
    ) {
    }

    loadPurchaseOrderItems(
        pageNumber: number,
        itemsPerPage: number,
        sortField: string | null = null,
        sortDirection: 'asc' | 'desc' | null = null,
        filters: Record<string, FilterValue> = {},
    ): Observable<PurchaseOrderItemCollection> {
        let params = new HttpParams()
            .set('page', pageNumber)
            .set('itemsPerPage', itemsPerPage);

        if (sortField && sortDirection) {
            params = params.set(`order[${sortField}]`, sortDirection);
        }

        for (const [field, value] of Object.entries(filters)) {
            if (this.isDateRangeFilterValue(value)) {
                if (value.from !== undefined) {
                    params = params.set(`${field}[from]`, value.from);
                }

                if (value.to !== undefined) {
                    params = params.set(`${field}[to]`, value.to);
                }

                continue;
            }

            if (Array.isArray(value)) {
                for (const arrayValue of value) {
                    params = params.append(`${field}[]`, arrayValue);
                }

                continue;
            }

            params = params.set(field, this.normalizeFilterValue(value));
        }

        return this.httpClient
            .get('/purchase-order-items', {params})
            .pipe(map((result: any) => this.hydrateCollection(result)));
    }

    private isDateRangeFilterValue(value: FilterValue): value is DateRangeFilterValue {
        return typeof value === 'object'
            && value !== null
            && !(value instanceof Date)
            && !Array.isArray(value)
            && ('from' in value || 'to' in value);
    }

    private normalizeFilterValue(value: string | number | Date): string | number {
        if (value instanceof Date) {
            return this.formatDateOnly(value);
        }

        return value;
    }

    private formatDateOnly(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    private hydrateCollection(result: any): PurchaseOrderItemCollection {
        const collection = new PurchaseOrderItemCollection();

        collection.setTotalItems(result['totalItems']);
        collection.setPurchaseOrderItems(
            result['member'].map((poData: any) => classTransformer.plainToInstance(PurchaseOrderItem, poData)),
        );

        return collection;
    }
}
